import {
  type UseMutationResult,
  type UseQueryResult,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import { adminMutationKeys, adminQueryKeys } from '@/hooks/admin/query-keys';
import { retryControlApi } from '@/hooks/admin/retry';
import { meshConnectivityService } from '@/services/control/mesh-connectivity-service';
import type {
  MeshConnectivity,
  MeshUserSyncReport,
  TenantMeshStatus,
} from '@/types/control/mesh-connectivity';

export function useMeshConnectivity(): UseQueryResult<MeshConnectivity, Error> {
  return useQuery<MeshConnectivity, Error>({
    queryKey: adminQueryKeys.meshConnectivity(),
    queryFn: ({ signal }) => meshConnectivityService.getConnectivity(signal),
    retry: retryControlApi,
  });
}

/**
 * Per-tenant mesh registration.
 *
 * **This one must not refetch on its own.** The endpoint fans out a live mesh
 * probe per tenant, five concurrent, which makes it the most expensive call on
 * the admin surface. `main.tsx` sets `refetchOnMount: 'always'` and
 * `refetchOnWindowFocus: 'always'` globally and both override `staleTime`, so
 * without these overrides the fan-out would re-fire on every mount and every
 * tab focus, forever, for every staff member with the tab open. Refreshing is
 * an explicit action instead.
 */
export function useTenantMeshStatus(): UseQueryResult<
  TenantMeshStatus[],
  Error
> {
  return useQuery<TenantMeshStatus[], Error>({
    queryKey: adminQueryKeys.tenantMeshStatus(),
    queryFn: ({ signal }) =>
      meshConnectivityService.listTenantMeshStatus(signal),
    staleTime: 60_000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    retry: retryControlApi,
  });
}

/** On demand only: `enabled` stays false until a row is expanded. */
export function useTenantMeshUserSync(
  tenantId: string | null
): UseQueryResult<MeshUserSyncReport, Error> {
  return useQuery<MeshUserSyncReport, Error>({
    queryKey: adminQueryKeys.tenantMeshUserSync(tenantId ?? undefined),
    queryFn: ({ signal }) =>
      meshConnectivityService.getUserSync(tenantId as string, signal),
    enabled: Boolean(tenantId),
    staleTime: 60_000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    retry: retryControlApi,
  });
}

export function useBackfillMeshUsers(
  tenantId: string
): UseMutationResult<MeshUserSyncReport, Error, void> {
  const queryClient = useQueryClient();

  return useMutation<MeshUserSyncReport, Error, void>({
    mutationKey: adminMutationKeys.backfillMeshUsers(tenantId),
    mutationFn: () => meshConnectivityService.backfillUsers(tenantId),
    onSuccess: (report) => {
      // The backfill response *is* a fresh report, so seed it rather than
      // making the user wait for a second round trip, then invalidate so any
      // other observer re-reads.
      queryClient.setQueryData(
        adminQueryKeys.tenantMeshUserSync(tenantId),
        report
      );
      queryClient.invalidateQueries({
        queryKey: adminQueryKeys.tenantMeshStatus(),
      });
    },
  });
}
